import type { ProcedureInitiative, ProcedureStep, Stored, User } from "@overmap-ai/common"
import { ConditionManager, FieldSchema } from "@overmap-ai/forms"

import type { ProcedureData } from "../../typings"
import { getProcedureStepCompletionStatus } from "../../utils/procedures"
import { CreatedByFilterDef } from "../defs/CreatedByFilterDef"
import { ProcedureInitiativeFilterDef } from "../defs/ProcedureInitiativeFilterDef"
import { ProcedureStepAssigneeFilterDef } from "../defs/ProcedureStepAssigneeFilterDef"
import { ProcedureStepCompletionStatusFilterDef } from "../defs/ProcedureStepCompletionStatusFilterDef"
import { ProcedureStepReviewerFilterDef } from "../defs/ProcedureStepReviewerFilterDef"
import { ProcedureStepReviewStatusFilterDef } from "../defs/ProcedureStepReviewStatusFilterDef"
import { SubmittedAtFilterDef } from "../defs/SubmittedAtFilterDef"
import { getFilterDefConfigsForFieldSchema } from "../defs/utils"
import { FilterSchema } from "./FilterSchema"
import type { AnyFilterSchemaDefConfig } from "./typings"
import { createFilterDefConfig } from "./utils"

interface ProcedureDataFilterSchemaConfig {
	procedureInitiatives: Stored<ProcedureInitiative>[]
	procedureTypeFieldSchema: FieldSchema
	procedureSteps: Stored<ProcedureStep>[]
	procedureStepFieldSchemasAndConditions: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	users: User[]
}

export const getProcedureDataFilterSchema = (config: ProcedureDataFilterSchemaConfig) => {
	const {
		procedureInitiatives,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		users,
	} = config

	const filterDefs: AnyFilterSchemaDefConfig<ProcedureData>[] = [
		createFilterDefConfig({
			filterDef: new CreatedByFilterDef(users),
			accessorFn: ({ procedure }) => procedure.created_by ?? -1,
		}),
		createFilterDefConfig({
			filterDef: new SubmittedAtFilterDef(),
			accessorFn: ({ procedure }) => new Date(procedure.submitted_at),
		}),
		createFilterDefConfig({
			filterDef: new ProcedureInitiativeFilterDef(procedureInitiatives),
			accessorFn: ({ procedure }) => procedure.procedure_initiative,
		}),
		...getFilterDefConfigsForFieldSchema<ProcedureData>(procedureTypeFieldSchema, (data) => [
			procedureTypeFieldSchema.deserializeValues(data.procedureFieldValues?.values ?? {}),
			data.procedureFieldValuesAttachments,
		]),
	]

	for (const procedureStep of procedureSteps) {
		const [fieldSchema, conditionManager] = procedureStepFieldSchemasAndConditions[procedureStep.uuid]

		filterDefs.push(
			createFilterDefConfig({
				filterDef: new ProcedureStepCompletionStatusFilterDef(procedureStep),
				accessorFn: (data) =>
					getProcedureStepCompletionStatus(
						fieldSchema,
						conditionManager,
						data.procedureStepFieldValues[procedureStep.uuid]?.values,
						!data.procedureStepFieldValues[procedureStep.uuid]?.published_at,
					),
			}),
			createFilterDefConfig<ProcedureData, ProcedureStepReviewStatusFilterDef>({
				filterDef: new ProcedureStepReviewStatusFilterDef(procedureStep),
				accessorFn: (data) => data.procedureStepFieldValuesReviews[procedureStep.uuid]?.type ?? null,
			}),
			createFilterDefConfig({
				filterDef: new ProcedureStepAssigneeFilterDef(procedureStep, users),
				accessorFn: (data) =>
					(data.procedureStepAssignees[procedureStep.uuid] ?? []).map(
						(procedureStepAssignee) => procedureStepAssignee.user,
					),
			}),
			createFilterDefConfig({
				filterDef: new ProcedureStepReviewerFilterDef(procedureStep, users),
				accessorFn: (data) =>
					(data.procedureStepReviewers[procedureStep.uuid] ?? []).map(
						(procedureStepReviewer) => procedureStepReviewer.user,
					),
			}),
			...getFilterDefConfigsForFieldSchema<ProcedureData>(fieldSchema, (data) => [
				fieldSchema.deserializeValues(data.procedureStepFieldValues[procedureStep.uuid]?.values ?? {}),
				data.procedureStepFieldValuesAttachments[procedureStep.uuid] ?? [],
			]),
		)
	}

	return FilterSchema.create<ProcedureData>({
		filterDefs: filterDefs,
	})
}
